/**
 * Service for handling WebSocket connection operations
 */
class ConnectionService {
  /**
   * Validates and formats WebSocket connection information
   * @param {Object|string} connectionInfo - The connection information to validate
   * @returns {Object|null} - Formatted connection information or null if invalid
   */
  validateConnectionInfo(connectionInfo) {
    try {
      // If connectionInfo is a string, try to parse it
      let parsedInfo = connectionInfo;
      if (typeof connectionInfo === 'string') {
        parsedInfo = JSON.parse(connectionInfo);
      }
      
      // Validate required fields
      if (!parsedInfo || typeof parsedInfo !== 'object') {
        console.log('ConnectionService: Invalid connection info - not an object');
        return null;
      }
      
      if (!parsedInfo.ip_address) {
        console.log('ConnectionService: Invalid connection info - missing ip_address');
        return null;
      }
      
      if (parsedInfo.port === undefined || parsedInfo.port === null) {
        console.log('ConnectionService: Invalid connection info - missing port');
        return null;
      }
      
      // Format according to README, and include optional path
      return {
        ip_address: parsedInfo.ip_address,
        port: parsedInfo.port,
        path: parsedInfo.path || '/' // Default to root path if not specified
      };
    } catch (error) {
      console.error('ConnectionService: Error validating connection info:', error);
      return null;
    }
  }
  
  /**
   * Formats POST data according to README format
   * @param {Object} data - The data to format
   * @returns {Object} - Formatted data
   */
  formatPostData(data) {
    const { token, userData, serverData, wsConnection } = data;
    
    // Validate and format WebSocket connection info
    console.log('ConnectionService: Raw wsConnection data:', JSON.stringify(wsConnection, null, 2));
    const formattedWsConnection = this.validateConnectionInfo(wsConnection);
    console.log('ConnectionService: Formatted wsConnection:', JSON.stringify(formattedWsConnection, null, 2));
    
    // Format user data
    const formattedUserData = userData && typeof userData === 'object' ? {
      user_id: userData.user_id || 'default_user',
      user_name: userData.user_name || 'Default User'
    } : {
      user_id: 'default_user',
      user_name: 'Default User'
    };
    
    // Format server data
    let formattedServerData = [];
    if (Array.isArray(serverData) && serverData.length > 0) {
      formattedServerData = serverData.map(server => ({
        server_id: server.server_id || `server_${Math.random().toString(36).substring(2, 9)}`,
        server_name: server.server_name || 'Default Server'
      }));
    } else {
      formattedServerData = [{
        server_id: 'default_server',
        server_name: 'Default Server'
      }];
    }
    
    // Create formatted data object
    const formattedData = {
      token: token || '',
      user_data: formattedUserData,
      server_data: formattedServerData,
      ws_connection: formattedWsConnection
    };
    
    console.log('ConnectionService: Formatted POST data:', JSON.stringify(formattedData, null, 2));
    return formattedData;
  }
  
  /**
   * Builds a WebSocket URL from connection info
   * @param {Object} connectionInfo - The connection information
   * @returns {string|null} - WebSocket URL or null if invalid
   */
  buildWebSocketUrl(connectionInfo) {
    const validatedInfo = this.validateConnectionInfo(connectionInfo);
    if (!validatedInfo) {
      return null;
    }
    
    // Support custom path if provided
    const path = validatedInfo.path || '/';
    
    // Add a configuration option that can be controlled via environment variable
    // When REACT_APP_USE_SECURE_WEBSOCKET is false, always use ws://
    // When it's true or undefined, follow the page protocol
    const useSecureWebsocket = process.env.REACT_APP_USE_SECURE_WEBSOCKET !== 'false';
    const protocol = useSecureWebsocket && window.location.protocol === 'https:' ? 'wss' : 'ws';
    
    // Special handling for common server scenarios
    let host = validatedInfo.ip_address;
    
    // Handle Docker/local IP addresses - if it's a private IP but we're accessing
    // from a remote location, we may need to use window.location.hostname instead
    if (/^(172\.1[6-9]|172\.2[0-9]|172\.3[0-1]|192\.168|10\.)/.test(host)) {
      console.log('ConnectionService: Detected private IP address:', host);
      
      // If the IP is in a private range and we're not on localhost,
      // try to use the current hostname instead
      if (typeof window !== 'undefined' && 
          window.location.hostname !== 'localhost' && 
          window.location.hostname !== '127.0.0.1') {
        
        console.log('ConnectionService: Using window.location.hostname instead of private IP');
        host = window.location.hostname;
      }
    }
    
    const wsUrl = `${protocol}://${host}:${validatedInfo.port}${path}`;
    console.log('ConnectionService: Built WebSocket URL:', wsUrl);
    return wsUrl;
  }
}

export default new ConnectionService();
